import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Avatar, Searchbar } from '@treezor/design-system';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { TFavorite, TFavoriteItemType } from '@/features/dashboard-users/store/support-user.model';

import { useAppDispatch } from '@/hooks/redux-helpers';
import { useFavorites } from '@/hooks/useFavorites';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { WalletStarred } from '@/features/wallets/components/WalletStarred';
import { setCurrentWallet } from '@/features/wallets/store/actions/wallet.actions';

import { useMatomo } from '@/features/matomo/useMatomo';

interface IFavoriteListProps {
  type: TFavoriteItemType;
  list: TFavorite[];
  onClose: () => void;
}

export function FavoriteList({ type, list, onClose }: IFavoriteListProps) {
  const { t } = useTranslation();

  const [filteredList, setFilteredList] = useState<TFavorite[]>(list);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { triggerEvent } = useMatomo();

  const { handleFavorite, maxFavorites } = useFavorites();

  const handleNavigation = useCallback(
    (favorite: TFavorite) => {
      if (favorite?.userId && favorite?.walletId) {
        dispatch(setCurrentWallet(favorite.userId, favorite.walletId));
        navigate(DashboardHelpers.generateProfilePath(favorite.userId, { walletId: favorite.walletId }));
        onClose();
      }
    },
    [dispatch, onClose, navigate],
  );

  const onSearch = useCallback(
    (value) => {
      if (value === '') {
        setFilteredList(list);
        return;
      }

      const filteredOptions = list.filter((listItem) => {
        const { name, iban, userName } = listItem;
        return [name, iban, userName]
          .filter(Boolean)
          .find((item) => item.toString().toLowerCase().includes(value.toLowerCase()));
      });

      setFilteredList(filteredOptions);
    },
    [list],
  );

  if (list.length === 0) {
    return (
      <EmptyState
        icon="search_v1"
        title={t('pages.dashboard.favorites.noFavorite')}
        state={t('pages.dashboard.favorites.addFavoriteMessage')}
        className="min-w-[320px]"
      />
    );
  }

  return (
    <div>
      <div className="flex px-5 py-[10px] items-center justify-between">
        <h6 className="typo-h6-bold text-secondary">
          {type === 'wallet' ? t('pages.dashboard.favorites.starredWallets') : type}
        </h6>
        <span className="typo-number-regular-m text-helper">{`${list.length}/${maxFavorites}`}</span>
      </div>
      <div className="flex px-5 py-[10px] items-center">
        <Searchbar type="text" onChange={onSearch} className="w-full" />
      </div>

      {filteredList?.length === 0 && (
        <EmptyState
          icon="search_v1"
          title={t('pages.dashboard.favorites.notFound')}
          state={t('pages.dashboard.favorites.notFoundMessage')}
          className="min-w-[320px]"
        />
      )}

      <div className="min-w-[320px]">
        {(filteredList || []).map((listItem: TFavorite) => (
          <div
            key={`favorite-item-${listItem.walletId}`}
            className="flex px-5 py-3 gap-3 cursor-pointer hover:bg-secondary justify-between"
            onClick={() => handleNavigation(listItem)}
          >
            <div className="flex items-start">
              <Avatar icon="account_balance_wallet" color="blue" />
            </div>
            <div className="flex flex-col w-full">
              <span className="typo-body-semibold-small text-secondary">
                {listItem?.name || t('pages.dashboard.favorites.noName')}
              </span>
              {listItem?.userName && (
                <span className="typo-body-regular-caption text-tertiary">{listItem.userName}</span>
              )}
              <span className="typo-body-regular-small text-secondary">{listItem?.iban}</span>
            </div>
            <div className="flex items-center">
              <WalletStarred
                isStarred
                shouldPreventDefault
                onClick={() => {
                  handleFavorite(listItem);
                  triggerEvent('starred-wallet', {
                    'starred-wallet-action': 'remove',
                    'starred-wallet-action-value': -1,
                  });
                  setFilteredList(filteredList.filter((item) => item.walletId !== listItem.walletId));
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
